import type { WorkflowSummary } from '@/api/workflows';

/**
 * Which cards the Originator dashboard renders for a set of workflows, and
 * which tab each one belongs to.
 *
 * Pure and separate from the component because the mapping is subtle enough to
 * have shipped a bug: a workflow can occupy MORE THAN ONE dashboard slot, the
 * rules differ per slot, and a workflow that matches no slot simply vanishes
 * with nothing on screen to say so.
 */

export const DASHBOARD_TABS = [
  'Draft',
  'Pending', // "Waiting for Approval"
  'PendingPublishing', // approved, not yet live
  'Published', // approved and live
  'Rejected',
] as const;

export type DashboardTab = (typeof DASHBOARD_TABS)[number];

export const isDashboardTab = (v: unknown): v is DashboardTab =>
  typeof v === 'string' && (DASHBOARD_TABS as readonly string[]).includes(v);

/**
 * One rendered card.
 *
 * - `inflight` — the working copy: whatever Draft/Pending/Rejected version the
 *   originator is editing.
 * - `published` — the version advisors are actually being served.
 * - `pendingApproval` — a re-approval the Approver has approved but not yet
 *   pushed live.
 */
export type CardView = {
  workflow: WorkflowSummary;
  view: 'inflight' | 'published' | 'pendingApproval';
};

/**
 * Explode each workflow into the cards it should render.
 *
 * The `pendingApproval` slot is the one that was missing. When a workflow is
 * re-approved on top of an already-live version, the domain deliberately parks
 * the new version in `PendingApprovedVersionNumber` and leaves
 * `CurrentVersionNumber` / `IsActive` pointing at the version still serving
 * advisors, so the live one keeps working during the staging window
 * (`WorkflowTemplate.Approve`, branch (b)). Keying the dashboard purely off
 * `currentVersionNumber` therefore rendered the workflow as its live
 * PREDECESSOR under Published and showed nothing at all for the approved
 * version — the Pending Publishing tab read 0 while the Approver's own
 * "Approved Workflows" tab showed it waiting to be published.
 */
export function deriveCards(items: WorkflowSummary[] | null | undefined): CardView[] {
  return (items ?? []).flatMap((w) => {
    const out: CardView[] = [];
    if (w.status !== 'Approved') out.push({ workflow: w, view: 'inflight' });
    // Suppressed when a staged approval would land in the same tab: an
    // approved-but-never-published version carrying a newer approval on top
    // would otherwise render twice in Pending Publishing and share one drag id.
    // The staged card wins, because it shows the newer version.
    if (w.currentVersionNumber > 0 && (w.isActive || !w.pendingApprovedVersionNumber)) {
      out.push({ workflow: w, view: 'published' });
    }
    if (w.pendingApprovedVersionNumber) out.push({ workflow: w, view: 'pendingApproval' });
    return out;
  });
}

/** Whether a card belongs to `tab`. */
export function matchesTab(c: CardView, tab: DashboardTab): boolean {
  // Pending Publishing holds both routes to "approved but not live": a
  // first-ever approval (its own current version, inactive) and a re-approval
  // staged over a live predecessor.
  if (tab === 'PendingPublishing') {
    return c.view === 'pendingApproval' || (c.view === 'published' && !c.workflow.isActive);
  }
  if (tab === 'Published') return c.view === 'published' && c.workflow.isActive;
  // Draft / Pending / Rejected are read off the underlying WorkflowStatus.
  return c.view === 'inflight' && c.workflow.status === tab;
}

/** The version label a card should show. */
export function cardVersionLabel(c: CardView): string {
  const w = c.workflow;
  // The staged card is labelled with the PENDING version, not the live one —
  // showing which version is waiting to go live is the entire point of it, and
  // borrowing currentVersionLabel would render it as a twin of the Published card.
  if (c.view === 'pendingApproval') return w.pendingApprovedVersionLabel || w.currentVersionLabel;
  if (c.view === 'published') return w.currentVersionLabel;
  return w.inProgressVersionLabel || w.currentVersionLabel;
}
